// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * NextOfKinEntryPage - /toegang
 * Verificatieflow voor nabestaanden en vertrouwenspersonen.
 * Sleutelcode + geboortedatum. Max 5 pogingen. Blokkade 24u.
 */
import { useState, useEffect } from 'react'
import { useNavigate }         from 'react-router-dom'
import { Leaf, Eye, EyeOff, AlertCircle, ShieldCheck, Lock } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useSessionStore } from '@/store'
import {
  verifyAndCreateSession,
  checkBlockStatus,
  VERIFY_FAILED_MESSAGE,
  type BlockStatus,
} from '@/services/verify'
import { ROUTES, HOME_ROUTE } from '@/lib/constants/routes'
import { APP_VERSION } from '@/lib/constants/version'

type Phase = 'form' | 'verifying' | 'blocked' | 'success'

export function NextOfKinEntryPage() {
  const navigate = useNavigate()
  const { setSession } = useSessionStore()

  const [awareness, setAwareness] = useState(false)  // P2.1: bewustwordingsstap
  const [rawCode,  setRawCode]  = useState('')
  const [dob,      setDob]      = useState('')
  const [showCode, setShowCode] = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [phase,    setPhase]    = useState<Phase>('form')
  const [, setBlockStatus] = useState<BlockStatus | null>(null)
  const [countdown, setCountdown] = useState(0)

  // Check blokkade bij laden
  useEffect(() => {
    checkBlockStatus().then(s => {
      if (s.blocked) {
        setPhase('blocked')
        setBlockStatus(s)
        setCountdown(Math.ceil(s.remainingMs / 1000))
      }
    })
  }, [])

  // Afteller
  useEffect(() => {
    if (phase !== 'blocked' || countdown <= 0) return
    const timer = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(timer)
          setPhase('form')
          setBlockStatus(null)
          return 0
        }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [phase, countdown])

  function formatCountdown(secs: number): string {
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
  }

  function formatCodeInput(val: string): string {
    // Auto-format XXXX-XXXX as user types
    const raw = val.replace(/[-–\s]/g, '').toUpperCase().slice(0, 8)
    if (raw.length > 4) return raw.slice(0, 4) + '-' + raw.slice(4)
    return raw
  }

  async function handleSubmit() {
    setError(null)

    const code = rawCode.replace(/[-–\s]/g, '').trim()
    if (code.length !== 8) {
      setError('Voer een geldige sleutelcode in (8 tekens, formaat: XXXX-XXXX).')
      return
    }
    if (!dob) {
      setError('Voer de geboortedatum van de profieleigenaar in.')
      return
    }

    setPhase('verifying')

    const result = await verifyAndCreateSession(rawCode, dob)

    if (result.blocked) {
      setPhase('blocked')
      setBlockStatus({
        blocked:      true,
        blockedUntil: result.blockedUntil,
        remainingMs:  result.remainingMs,
        attemptNumber: result.attemptNumber,
      })
      setCountdown(Math.ceil(result.remainingMs / 1000))
      return
    }

    if (!result.success || !result.sessionToken || !result.userId || !result.expiresAt) {
      setPhase('form')
      const remaining = MAX_ATTEMPTS - (result.attemptNumber ?? 1)
      if (remaining <= 1 && remaining > 0) {
        setError(`${VERIFY_FAILED_MESSAGE} Nog ${remaining} poging${remaining === 1 ? '' : 'en'} voordat uw toegang wordt geblokkeerd.`)
      } else {
        setError(VERIFY_FAILED_MESSAGE)
      }
      return
    }

    // Succes - intentioneel rustmoment
    setPhase('success')
    setSession(result.sessionToken, result.userId, result.expiresAt)

    setTimeout(() => {
      navigate(ROUTES.NEXT_OF_KIN, { replace: true })
    }, 3000)
  }

  const MAX_ATTEMPTS = 5

  // ── Geblokkeerd scherm ────────────────────────────────────────────────────
  if (phase === 'blocked') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-sand px-5">
        <div className="w-full max-w-sm flex flex-col items-center gap-6 text-center">
          {/* Terug naar beginscherm */}
          <div className="self-start">
            <button
              onClick={() => navigate(HOME_ROUTE)}
              className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors"
            >
              <span className="text-base leading-none">←</span>
              <span>Terug naar beginscherm</span>
            </button>
          </div>
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-status-alertBg">
            <Lock className="h-8 w-8 text-status-alertText" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-ink mb-2">Toegang geblokkeerd</h1>
            <p className="text-sm text-ink-3 leading-relaxed">
              Wegens te veel mislukte pogingen is uw apparaat tijdelijk geblokkeerd.
            </p>
          </div>
          {countdown > 0 && (
            <div className="w-full rounded-2xl bg-white border border-sand-300 px-6 py-5">
              <p className="text-xs text-ink-4 mb-2">Nog geblokkeerd voor:</p>
              <p className="text-3xl font-mono font-bold text-ink tabular-nums">
                {formatCountdown(countdown)}
              </p>
            </div>
          )}
          <p className="text-xs text-ink-4 leading-relaxed">
            Bent u de eigenaar van het profiel?{' '}
            <button onClick={() => navigate(ROUTES.LOGIN)} className="text-sage-700 underline">
              Log hier in
            </button>
          </p>
        </div>
      </div>
    )
  }

  // ── Succes scherm ─────────────────────────────────────────────────────────
  if (phase === 'success') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-sand px-5">
        <div className="w-full max-w-sm flex flex-col items-center gap-6 text-center">
          {/* Terug naar beginscherm */}
          <div className="self-start">
            <button
              onClick={() => navigate(HOME_ROUTE)}
              className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors"
            >
              <span className="text-base leading-none">←</span>
              <span>Terug naar beginscherm</span>
            </button>
          </div>
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-sage-100">
            <ShieldCheck className="h-8 w-8 text-sage-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-ink mb-2">Verificatie geslaagd</h1>
            <p className="text-sm text-ink-3">U wordt nu doorgestuurd...</p>
          </div>
          <div className="h-1.5 w-48 rounded-full bg-sand-300 overflow-hidden">
            <div className="h-full rounded-full bg-sage-500 animate-pulse" style={{ width: '100%', transition: 'width 3s linear' }} />
          </div>
        </div>
      </div>
    )
  }

  // ── P2.1: Bewustwordingsstap vóór toegang ────────────────────────────────────
  if (!awareness) {
    return (
      <div className="min-h-screen bg-sand px-5">
        <div className="w-full max-w-sm flex flex-col gap-6 py-8 mx-auto">
          {/* Terug naar beginscherm */}
          <div className="self-start">
            <button onClick={() => navigate(HOME_ROUTE)}
              className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors">
              <span className="text-base leading-none">←</span>
              <span>Terug naar beginscherm</span>
            </button>
          </div>

          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-moss shadow-card">
              <ShieldCheck className="h-8 w-8 text-white" />
            </div>
          </div>

          <div className="text-center">
            <h1 className="text-xl font-semibold text-ink mb-2">Toegang vertrouwenspersoon</h1>
            <p className="text-sm text-ink-3 leading-relaxed">
              U staat op het punt de persoonlijke gegevens in te zien die voor u zijn vastgelegd.
            </p>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl bg-white border border-sand-300 p-5">
            <p className="text-sm font-semibold text-ink">Voordat u verdergaat</p>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 text-base">🔒</span>
              <p className="text-xs text-ink-3 leading-relaxed">
                Deze toegang is bedoeld voor de situatie waarvoor u de sleutelcode heeft ontvangen.
                Gebruik de sleutelcode uitsluitend overeenkomstig de afspraken die u heeft gemaakt
                met de eigenaar van dit profiel.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 text-base">📋</span>
              <p className="text-xs text-ink-3 leading-relaxed">
                WilsApp verifieert niet of de profieleigenaar is overleden. Het gebruik van de
                sleutelcode is een vertrouwenshandeling.
              </p>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={awareness} onChange={e => setAwareness(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-sand-300 text-sage-600 focus:ring-sage-400 flex-shrink-0" />
            <span className="text-sm text-ink-2">
              Ik verklaar dat ik deze toegang gebruik overeenkomstig de afspraken met de eigenaar van dit profiel.
            </span>
          </label>

          <button
            disabled={!awareness}
            className="w-full rounded-xl bg-moss text-white text-sm font-semibold py-3.5 hover:bg-moss-light disabled:opacity-40 disabled:pointer-events-none transition-all"
            onClick={() => setAwareness(true)}>
            Doorgaan naar toegang
          </button>
        </div>
      </div>
    )
  }

  // ── Verificatieformulier ──────────────────────────────────────────────────
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-sand px-5">
      <div className="w-full max-w-sm flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-moss">
            <Leaf className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">WilsApp</h1>
            <p className="text-sm text-ink-4 mt-1">Toegang voor vertrouwenspersonen en nabestaanden</p>
          </div>
        </div>

        {/* Wat is WilsApp - uitleg voor wie de app niet kent */}
        <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
          <p className="text-sm font-semibold text-blue-900 mb-1">Wat is WilsApp?</p>
          <p className="text-xs text-blue-900/90 leading-relaxed">
            WilsApp is een app waarin iemand zijn wensen en belangrijke informatie voor nabestaanden vastlegt.
            U bent door de profieleigenaar aangewezen als vertrouwenspersoon. Met de sleutelcode uit de
            overdrachtsbrief en de geboortedatum van de eigenaar krijgt u toegang tot <strong>uitsluitend</strong> de
            onderdelen die de eigenaar voor u heeft vrijgegeven. WilsApp controleert niet of iemand is overleden -
            het gebruik van de code is een vertrouwenshandeling.
          </p>
        </div>

        {/* Foutmelding */}
        {error && (
          <div className="flex items-start gap-3 rounded-xl border border-status-alertBorder bg-status-alertBg px-4 py-3">
            <AlertCircle className="h-4 w-4 text-status-alertText flex-shrink-0 mt-0.5" />
            <p className="text-sm text-status-alertText">{error}</p>
          </div>
        )}

        {/* Formulier */}
        <div className="flex flex-col gap-4 rounded-2xl bg-white border border-sand-300 p-6 shadow-card">
          {/* Sleutelcode veld */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">
              Sleutelcode
            </label>
            <div className="relative">
              <input
                type={showCode ? 'text' : 'password'}
                value={rawCode}
                onChange={e => setRawCode(formatCodeInput(e.target.value))}
                onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
                placeholder="XXXX-XXXX"
                className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 pr-12 text-lg font-mono tracking-widest text-ink placeholder:text-ink-4 focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                disabled={phase === 'verifying'}
              />
              <button
                type="button"
                onClick={() => setShowCode(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-lg text-ink-4 hover:bg-sand-100"
              >
                {showCode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-ink-4 mt-1">De code staat in de overdrachtsbrief van de profileigenaar.</p>
          </div>

          {/* Geboortedatum */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">
              Geboortedatum profieleigenaar
            </label>
            <input
              type="date"
              value={dob}
              onChange={e => setDob(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
              className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent"
              disabled={phase === 'verifying'}
            />
            <p className="text-xs text-ink-4 mt-1">Geboortedatum van de persoon die dit profiel heeft aangemaakt.</p>
          </div>

          <Button
            fullWidth
            onClick={handleSubmit}
            loading={phase === 'verifying'}
            disabled={phase === 'verifying' || !rawCode || !dob}
          >
            Toegang aanvragen
          </Button>
        </div>

        {/* Privacy + eigen login */}
        <div className="flex flex-col gap-2 text-center">
          <p className="text-xs text-ink-4">
            Bent u de eigenaar van dit profiel?{' '}
            <button onClick={() => navigate(ROUTES.LOGIN)} className="text-sage-700 underline">
              Log hier in
            </button>
          </p>
          <p className="text-xs text-ink-4 leading-relaxed">
            Uw verificatiegegevens worden nooit opgeslagen.
            Na 5 mislukte pogingen wordt toegang tijdelijk geblokkeerd.
          </p>
        </div>

        {/* Versienummer - zichtbaar voor testers */}
        <p className="text-xs text-ink-4 text-center mt-2 font-mono">
          {APP_VERSION}
        </p>

      </div>
    </div>
  )
}
